import React, { useEffect, useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Rating,
  TextField,
  Tooltip,
  Typography,
  Card,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { setInPageLoading, setIsLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import {
  FetchDataSuccessAction,
  FetchDataFailureAction,
  fetchData,
  postData,
} from "../../store/actions";

import {
  skillQuestionsDetail,
  viewEmployeeSkillResponse,
  viewSkillWiseEmployeeResponse,
  viewActiveUsers,
  getDirectReports,
  managerVerifySkillResponse,
  designationSkillProfile,
  employeeSectionPercentage,
  skillRemark,
} from "../../Service/Apis";

import {
  EmployeeSkillSetType,
  SkillWiseEmployeeResponse,
} from "./Types/SkillTypes";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";
import Loading from "../../components/Loading/Loading";
import { useCustomToast } from "../../components/Methods/SnackBar";

interface Employeetype {
  id: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  gender: string;
  roles: [];
  designation: string;
  designation_id?: string;
  date_of_birth: string;
  date_of_joining: string;
  date_of_confirmation: string;
  date_of_exit: string;
  email: string;
  password: string;
}

const ratingLabel = [
  { label: "Awareness", color: "#FF5C5C" },
  { label: "Novice", color: "#5C8CFF" },
  { label: "Competent", color: "#5CFF6E" },
  { label: "Professional", color: "#FF9F5C" },
  { label: "Expert", color: "#B85CFF" },
];

// ── Section types ─────────────────────────────────────────────────────────

interface SectionQuestion {
  skill_question_id: string;
  question: string;
  employee_skill_response: string | null;
  manager_skill_response: string | null;
  manager_verified?: boolean;
}

interface SectionSkill {
  skill_id: string;
  skill_name: string;
  percentage: string;
  questions: SectionQuestion[];
}

interface ProfileSection {
  section_id: string;
  section_name: string;
  weightage: number;
  skills: SectionSkill[];
}

interface SectionScore {
  section_id: string;
  section_name: string;
  weightage: number;
  employee_section_score: number;
  employee_section_contribution: number;
  manager_section_score: number | null;
  manager_section_contribution: number;
  manager_rated: boolean;
}

interface PercentageData {
  overall_employee_percentage: number;
  overall_manager_percentage: number;
  sections: SectionScore[];
}

// ── Component ──────────────────────────────────────────────────────────────

const ReviewEmployeesSkills = () => {
  // State Hooks
  const [employeesData, setEmployeesData] = useState<Employeetype[]>([]);
  const [skillsData, setSkillsData] = useState<EmployeeSkillSetType[]>([]);

  const [employee, setEmployee] = useState<Employeetype | null>(null);
  const [skill, setSkill] = useState<EmployeeSkillSetType | null>(null);

  const [data, setData] = useState<EmployeeSkillSetType[]>([]);
  const [skillEmployeeResponseData, setSkillEmployeeResponseData] = useState<
    SkillWiseEmployeeResponse[]
  >([]);

  // Section-related state (employee-wise view)
  const [sections, setSections] = useState<ProfileSection[]>([]);
  const [percentages, setPercentages] = useState<PercentageData | null>(null);
  const [employeeRemark, setEmployeeRemark] = useState<string | null>(null);

  // Skill-wise expanded employee id
  const [expandedSkillEmpId, setExpandedSkillEmpId] = useState<string | number | null>(null);
  const toggleSkillEmpExpand = (id: any) => {
    setExpandedSkillEmpId((prev) => (prev === id ? null : id));
  };

  // Employee-wise inline expand id (match Your Skills behavior)
  const [expandedEmpSkillId, setExpandedEmpSkillId] = useState<string | number | null>(null);

  // Section expand/collapse (employee-wise view)
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>(null);

  const [isSkillWiseReview, setIsSkillWiseReview] = useState<boolean>(false);
  const [isEmployeeWiseReview, setIsEmployeeWiseReviewq] =
    useState<boolean>(true);

  // Redux Hooks
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const isLoading = useSelector((state: any) => state.loading.inPageLoading);
  const selectedRole = useSelector((state: any) => state.auth.selectedRole);
  const showToast = useCustomToast();

  const handleRadioChange = (event: any) => {
    const { name, checked } = event.target;
    setIsSkillWiseReview(name === "skillwisereview" ? checked : !checked);
    setIsEmployeeWiseReviewq(
      name === "employeewisereview" ? checked : !checked
    );
    setEmployee(null);
    setSkill(null);
    setExpandedEmpSkillId(null);
  };

  // Toggle section expand/collapse
  const toggleSection = (sectionId: string) => {
    setExpandedSectionId((prev) => {
      setExpandedEmpSkillId(null);
      return prev === sectionId ? null : sectionId;
    });
  };

  // Toggle inline expand for a skill
  const toggleEmpExpand = (skillId: any) => {
    setExpandedEmpSkillId((prev) => (prev === skillId ? null : skillId));
  };

  // Save manager verification for currently expanded skill
  const handleManagerSaveInline = async (emp: any, index: number) => {
    const payload = {
      user_id: employee?.id,
      responses: (emp.questions as any).map((q: any) => ({
        skill_question_id: q.skill_question_id,
        manager_skill_response: q.manager_skill_response ?? null,
        manager_verified: q.manager_verified ?? false,
      })),
    } as any;
    try {
      await dispatch2(postData(managerVerifySkillResponse, payload));
      // Refresh responses + percentages
      const [refreshed, newPercentages]: any[] = await Promise.all([
        dispatch2(fetchData(`${viewEmployeeSkillResponse}?user_id=${employee?.id}`)),
        dispatch2(fetchData(`${employeeSectionPercentage}?employee_id=${employee?.id}`)),
      ]);
      setData(refreshed as EmployeeSkillSetType[]);
      if (newPercentages) setPercentages(newPercentages as PercentageData);
      showToast("Saved", "success", true);
      setExpandedEmpSkillId(null);
      setTimeout(() => {
        const el = document.getElementById(`emp-skill-${emp.skill_id}`);
        if (el) {
          const headerOffset = 80;
          const rect = el.getBoundingClientRect();
          const y = rect.top + window.scrollY - headerOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 0);
    } catch (e) {}
  };

  //Handle Employee Change Function
  const handleEmployeeChange = (e: any, values: any) => {
    setEmployee(values);
    setSections([]);
    setPercentages(null);
    setEmployeeRemark(null);
    setExpandedSectionId(null);
    setExpandedEmpSkillId(null);
  };

  const handleSkillChange = (e: any, values: any) => {
    setSkill(values);
  };

  const toRatingNumber = (value: string | null | undefined) => {
    const parsed = parseInt(value || "0", 10);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Deviation (%) between manager and employee ratings.
  // On a 1–5 scale, max possible gap is 4 stars.
  // Show only when both employee and manager ratings are present.
  const getRatingVariation = (
    employeeRating: string | null | undefined,
    managerRating: string | null | undefined
  ): number | null => {
    const employee = toRatingNumber(employeeRating);
    const manager = toRatingNumber(managerRating);
    if (employee <= 0 || manager <= 0) return null;
    const starDifference = Math.abs(manager - employee);
    return (starDifference / 4) * 100;
  };

  const getVariationText = (variation: number) => {
    if (variation === 0) return "Deviation: 0%";
    return `Deviation: ${variation.toFixed(0)}%`;
  };

  const getVariationNumberText = (variation: number, isOverall = false) => {
    const starDifference = (variation * 4) / 100;
    const formatted = isOverall
      ? starDifference.toFixed(2)
      : Number.isInteger(starDifference)
      ? starDifference.toString()
      : starDifference.toFixed(2);
    return isOverall
      ? `Overall difference: ${formatted}`
      : `Difference: ${formatted}`;
  };

  const getVariationColor = (variation: number) => {
    if (variation === 0) return "success.main";
    if (variation <= 25) return "info.main";
    if (variation <= 50) return "warning.main";
    return "error.main";
  };

  // Calculate Progress Function
  const calculateProgress = (questions: any) => {
    const attemptedCount = questions.filter(
      (que: any) => que.employee_skill_response !== null
    ).length;
    return (attemptedCount / questions.length) * 100;
  };

  useEffect(() => {
    // API Call get the User Details.
    const fetchUserDetails = async () => {
      try {
        dispatch(setIsLoading(true));
        const endpoint = selectedRole?.toLowerCase() === "manager" ? getDirectReports : viewActiveUsers;
        const response = await dispatch2(fetchData(`${endpoint}`));
        const list = Array.isArray(response?.direct_reports) ? response.direct_reports : response;
        setEmployeesData(list as Employeetype[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setIsLoading(false));
      }
    };

    fetchUserDetails();

    const fetchSkillsDetails = async () => {
      try {
        dispatch(setInPageLoading(true));
        const response = await dispatch2(fetchData(`${skillQuestionsDetail}`));
        setSkillsData(response as EmployeeSkillSetType[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setInPageLoading(false));
      }
    };

    fetchSkillsDetails();
  }, [selectedRole]);

  useEffect(() => {
    if (!employee) return;

    const loadEmployeeData = async () => {
      dispatch(setInPageLoading(true));
      try {
        const designationId = (employee as any).designation_id || (employee as any).designation;

        const [responsesRes, profileRes, percentagesRes, remarkRes]: any[] = await Promise.all([
          dispatch2(fetchData(`${viewEmployeeSkillResponse}?user_id=${employee.id}`)),
          designationId
            ? dispatch2(fetchData(`${designationSkillProfile}?designation_id=${designationId}`))
            : Promise.resolve(null),
          dispatch2(fetchData(`${employeeSectionPercentage}?employee_id=${employee.id}`)),
          dispatch2(fetchData(`${skillRemark}?employee_id=${employee.id}`)),
        ]);

        setData(responsesRes as EmployeeSkillSetType[]);
        if (percentagesRes) setPercentages(percentagesRes as PercentageData);
        if (remarkRes?.remark != null) setEmployeeRemark(remarkRes.remark);
        else setEmployeeRemark(null);

        // Build response lookup for merging into sections
        const responseMap: Record<string, Record<string, SectionQuestion>> = {};
        if (Array.isArray(responsesRes)) {
          (responsesRes as EmployeeSkillSetType[]).forEach((skill: any) => {
            responseMap[skill.skill_id] = {};
            skill.questions.forEach((q: any) => {
              responseMap[skill.skill_id][q.skill_question_id] = q;
            });
          });
        }

        if (profileRes?.sections) {
          const merged: ProfileSection[] = profileRes.sections.map((sec: any) => ({
            ...sec,
            skills: sec.skills.map((sk: any) => {
              const skillResponses = responseMap[sk.skill_id] || {};
              const responseSkill = (responsesRes as EmployeeSkillSetType[])?.find(
                (r: any) => r.skill_id === sk.skill_id
              );
              return {
                ...sk,
                percentage: (responseSkill as any)?.percentage || "0",
                questions: sk.questions.map((q: any) => ({
                  ...q,
                  employee_skill_response:
                    skillResponses[q.skill_question_id]?.employee_skill_response ?? null,
                  manager_skill_response:
                    (skillResponses[q.skill_question_id] as any)?.manager_skill_response ?? null,
                  manager_verified:
                    (skillResponses[q.skill_question_id] as any)?.manager_verified ?? false,
                })),
              };
            }),
          }));
          setSections(merged);
        }
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setInPageLoading(false));
      }
    };

    loadEmployeeData();
  }, [employee]);

  useEffect(() => {
    const fetchSkillEmployeeResponse = async () => {
      try {
        dispatch(setInPageLoading(true));
        const response = await dispatch2(
          fetchData(
            `${viewSkillWiseEmployeeResponse}?skill_id=${skill?.skill_id}`
          )
        );
        setSkillEmployeeResponseData(response as SkillWiseEmployeeResponse[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setInPageLoading(false));
      }
    };
    if (skill) {
      fetchSkillEmployeeResponse();
    }
  }, [skill]);

  // Loading Spinner Component
  if (isLoading) {
    return (
      <>
        <Loading isLoading={isLoading as boolean} />
      </>
    );
  }

  // Main Component

  return (
    <>
      <Box id="main-component" sx={{ mt: 2, mb: 10 }}>
          <Box sx={{ m: 2, p: 1, display: "flex", justifyContent: "right" }}>
            <RadioGroup>
              <Box sx={{ display: "flex" }}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={isEmployeeWiseReview}
                      onChange={handleRadioChange}
                      name="employeewisereview"
                    />
                  }
                  label="Employee Review"
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={isSkillWiseReview}
                      onChange={handleRadioChange}
                      name="skillwisereview"
                    />
                  }
                  label="Skills Review"
                />
              </Box>
            </RadioGroup>
          </Box>
          {isEmployeeWiseReview ? (
            <Box>
              <Autocomplete
                size="small"
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  option.first_name + " " + option.last_name
                }
                value={employee}
                onChange={handleEmployeeChange}
                id="employee-autocomplete"
                options={employeesData}
                sx={{ width: "60%", mx: "auto" }}
                renderInput={(params) => <TextField {...params} label="Select Employee Name" />}
              />
              {employee === null ? null : (
                <Box sx={{ pt: 2 }}>
                  {sections.length === 0 && data.length === 0 ? (
                    <Box sx={{ mt: 12, display: "flex", justifyContent: "center" }}>
                      <NothingToDisplayComponent />
                    </Box>
                  ) : (
                    <>
                      {/* ── Overall score chips ── */}
                      {percentages && (
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
                          <Chip
                            icon={percentages.overall_employee_percentage >= 60 ? <CheckCircleIcon /> : <WarningAmberIcon />}
                            label={`Employee Overall: ${percentages.overall_employee_percentage.toFixed(1)}%`}
                            color={percentages.overall_employee_percentage >= 60 ? "success" : "warning"}
                            variant="outlined"
                            sx={{ fontSize: 14, px: 1, py: 2.5 }}
                          />
                          {percentages.overall_manager_percentage > 0 && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={`Manager Overall: ${percentages.overall_manager_percentage.toFixed(1)}%`}
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: 14, px: 1, py: 2.5 }}
                            />
                          )}
                        </Box>
                      )}

                      {/* ── Column headers ── */}
                      <Box sx={{ width: "98%", display: "flex", mx: 2, mt: 2 }}>
                        <Box sx={{ width: "20%" }} />
                        <Box sx={{ width: "70%", display: "flex" }}>
                          <Box sx={{ width: "90%", mr: 2.7, ml: 1, display: "flex", justifyContent: "center", textAlign: "center" }}>
                            {ratingLabel.map((rl, i) => (
                              <Box key={i} sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "20%" }}>
                                <Typography sx={{ mr: 0.3 }} fontSize={13} fontWeight="bold">{rl.label}</Typography>
                              </Box>
                            ))}
                          </Box>
                          <Box sx={{ width: "10%", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                            <Typography variant="caption" fontWeight="bold">Avg Score</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography fontSize={13} fontWeight="bold">Questions</Typography>
                        </Box>
                      </Box>

                      {/* ── Sections ── */}
                      {sections.length > 0 ? sections.map((section) => {
                        const sectionScore = percentages?.sections.find(
                          (s) => s.section_id === section.section_id
                        );
                        return (
                          <Box key={section.section_id} sx={{ mb: 3 }}>
                            {/* Section header — clickable to expand/collapse */}
                            <Box
                              onClick={() => toggleSection(section.section_id)}
                              sx={{ mx: 2, px: 3, py: 1.5, backgroundColor: "primary.main", borderRadius: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, cursor: "pointer", userSelect: "none" }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <ChevronRightIcon
                                  sx={{
                                    color: "white",
                                    transform: expandedSectionId === section.section_id ? "rotate(90deg)" : "none",
                                    transition: "transform 0.2s",
                                  }}
                                />
                                <Typography variant="h6" fontWeight="bold" color="white">
                                  {section.section_name}
                                  <Typography component="span" variant="body2" color="white" sx={{ ml: 1, opacity: 0.85 }}>
                                    (Weightage: {section.weightage}%)
                                  </Typography>
                                </Typography>
                              </Box>
                              {sectionScore && (
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                  <Chip size="small" label={`Employee: ${sectionScore.employee_section_score.toFixed(1)}% → ${sectionScore.employee_section_contribution.toFixed(1)}%`} sx={{ backgroundColor: "white", fontWeight: "bold" }} />
                                  {sectionScore.manager_rated && sectionScore.manager_section_score != null && (
                                    <Chip size="small" label={`Manager: ${sectionScore.manager_section_score.toFixed(1)}% → ${sectionScore.manager_section_contribution.toFixed(1)}%`} sx={{ backgroundColor: "white", fontWeight: "bold" }} />
                                  )}
                                </Box>
                              )}
                            </Box>

                            {/* Skills in section — only when section is expanded */}
                            {expandedSectionId === section.section_id && section.skills.map((emp: any, index: number) => {
                              const totalQuestions = emp.questions.length;
                              const totalPossible = totalQuestions * 5;
                              const managerStars = emp.questions.reduce((sum: number, q: any) => {
                                const v = parseInt(q?.manager_skill_response || "0", 10);
                                return sum + (isNaN(v) ? 0 : v);
                              }, 0);
                              const managerPercentage = totalPossible > 0 ? (managerStars / totalPossible) * 100 : 0;
                              const managerRatedCount = emp.questions.filter((q: any) => q?.manager_skill_response != null && q?.manager_skill_response !== "0").length;
                              const isManagerComplete = managerRatedCount === totalQuestions;
                              const empAnswered = emp.questions.filter((q: any) => q.employee_skill_response !== null).length;
                              const isExpanded = expandedEmpSkillId === emp.skill_id;
                              const variationValues = (emp.questions as any[])
                                .map((q: any) =>
                                  getRatingVariation(q.employee_skill_response, q.manager_skill_response)
                                )
                                .filter((v): v is number => v !== null);
                              const avgVariation =
                                variationValues.length > 0
                                  ? variationValues.reduce((sum, v) => sum + v, 0) / variationValues.length
                                  : null;
                              const totalDeviationStars = variationValues.reduce(
                                (sum, v) => sum + (v * 4) / 100,
                                0
                              );

                              return (
                                <Card key={emp.skill_id} id={`emp-skill-${emp.skill_id}`} sx={{ m: 2, p: 2, pl: 4 }} elevation={4}>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                    <Box sx={{ width: "20%" }}>
                                      <Typography variant="h6" color="primary" fontWeight="bold">{emp.skill_name}</Typography>
                                      {avgVariation !== null && (
                                        <Tooltip title={`Overall difference: ${totalDeviationStars.toFixed(2)}`}>
                                          <Typography
                                            variant="caption"
                                            sx={{ color: getVariationColor(avgVariation), fontWeight: "bold" }}
                                          >
                                            Avg {getVariationText(parseFloat(avgVariation.toFixed(2)))}
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    <Box sx={{ width: "70%", mx: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="caption" fontWeight="bold" sx={{ width: 70, textAlign: "right" }}>Employee</Typography>
                                        <Box sx={{ width: "100%", position: "relative" }}>
                                          <LinearProgress variant="determinate" value={parseFloat(emp.percentage)} sx={{ flexGrow: 1, mr: 2 }} />
                                          <div className="skill-info-color" style={{ position: "absolute", top: "50%", transform: "translate(-50%,-50%)", left: `${parseFloat(emp.percentage) > 50 ? parseFloat(emp.percentage) - 1.5 : parseFloat(emp.percentage)}%`, width: 12, height: 12, borderRadius: "50%" }} />
                                        </Box>
                                        <Typography variant="body2" fontWeight="bold">{((parseFloat(emp.percentage) / 100) * 5).toFixed(1)}/5</Typography>
                                      </Box>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="caption" fontWeight="bold" sx={{ width: 70, textAlign: "right" }}>Manager</Typography>
                                        <Box sx={{ width: "100%", position: "relative" }}>
                                          <LinearProgress variant="determinate" value={managerPercentage} sx={{ flexGrow: 1, mr: 2, ...(!isManagerComplete ? { backgroundColor: "#ffebee", "& .MuiLinearProgress-bar": { backgroundColor: "#d32f2f" } } : {}) }} />
                                          <div className="skill-info-color" style={{ position: "absolute", top: "50%", transform: "translate(-50%,-50%)", left: `${managerPercentage > 50 ? managerPercentage - 1.5 : managerPercentage}%`, width: 12, height: 12, borderRadius: "50%", ...(!isManagerComplete ? { backgroundColor: "#d32f2f" } : {}) }} />
                                        </Box>
                                        <Typography variant="body2" fontWeight="bold">{((managerPercentage / 100) * 5).toFixed(1)}/5</Typography>
                                      </Box>
                                    </Box>
                                    <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "right" }}>
                                      <Typography variant="h6" color={empAnswered === totalQuestions ? "success" : "error"}>{empAnswered}/{totalQuestions}</Typography>
                                      <ChevronRightIcon fontSize="medium" sx={{ cursor: "pointer", ml: 1, transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} onClick={() => toggleEmpExpand(emp.skill_id)} />
                                    </Box>
                                  </Box>

                                  {/* Inline questions */}
                                  {isExpanded && (
                                    <Box sx={{ mt: 2, p: 1 }}>
                                      {emp.questions.map((question: any, questionIndex: number) => (
                                        <Card key={question.skill_question_id} sx={{ mb: 2, p: 2, boxShadow: "0px 0px 10px rgba(0,0,0,0.1)", border: "1px solid #ccc" }} elevation={5}>
                                          {(() => {
                                            const variation = getRatingVariation(
                                              question.employee_skill_response,
                                              question.manager_skill_response
                                            );
                                            return variation !== null ? (
                                              <Tooltip title={getVariationNumberText(variation)}>
                                                <Typography
                                                  variant="caption"
                                                  sx={{ color: getVariationColor(variation), fontWeight: "bold", ml: 1 }}
                                                >
                                                  {getVariationText(variation)}
                                                </Typography>
                                              </Tooltip>
                                            ) : null;
                                          })()}
                                          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", alignItems: "center", columnGap: 2, m: 0.5, p: 0.5 }}>
                                            <Box>
                                              <Typography fontWeight="bold" variant="h6" sx={{ textAlign: "justify", wordBreak: "break-word" }}>{questionIndex + 1}) {question.question}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                              <Typography align="center" variant="caption" fontWeight="bold">Employee</Typography>
                                              <Rating value={parseInt(question.employee_skill_response || "0")} readOnly size="large" className="rating-color" />
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                              <Typography align="center" variant="caption" fontWeight="bold">Manager</Typography>
                                              <Rating
                                                value={parseInt(question.manager_skill_response || "0")}
                                                readOnly={selectedRole?.toLowerCase() !== "manager"}
                                                onChange={(_, value) => {
                                                  if (selectedRole?.toLowerCase() !== "manager") return;
                                                  const normalized = value && value > 0 ? value.toString() : null;
                                                  setSections((prev) => prev.map((sec) => ({
                                                    ...sec,
                                                    skills: sec.skills.map((sk: any) => {
                                                      if (sk.skill_id !== emp.skill_id) return sk;
                                                      return {
                                                        ...sk,
                                                        questions: sk.questions.map((qq: any) =>
                                                          qq.skill_question_id === question.skill_question_id
                                                            ? { ...qq, manager_skill_response: normalized, manager_verified: !!value && value > 0 }
                                                            : qq
                                                        ),
                                                      };
                                                    }),
                                                  })));
                                                }}
                                                size="large"
                                                className="rating-color"
                                              />
                                            </Box>
                                          </Box>
                                        </Card>
                                      ))}
                                      {selectedRole?.toLowerCase() === "manager" && (
                                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                          <Button variant="contained" onClick={() => handleManagerSaveInline(emp, index)}>Save</Button>
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </Card>
                              );
                            })}
                          </Box>
                        );
                      }) : (
                        <Box sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center" }}>
                          <Typography variant="body1" color="text.secondary">
                            No skill sections are configured for this employee&apos;s designation.
                          </Typography>
                        </Box>
                      )}

                      {/* ── Employee Remarks (read-only) ── */}
                      <Box sx={{ mx: 4, mt: 4 }}>
                        <Divider sx={{ mb: 3 }} />
                        <Typography variant="h6" fontWeight="bold" mb={1}>Additional Skills / Remarks</Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          Extra skills the employee mentioned that are not in the listed sections.
                        </Typography>
                        {employeeRemark ? (
                          <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, backgroundColor: "grey.50" }}>
                            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{employeeRemark}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            No additional remarks provided by the employee.
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Autocomplete
                size="small"
                isOptionEqualToValue={(option, value) => option.skill_id === value.skill_id}
                getOptionLabel={(option) => option.skill_name}
                value={skill}
                onChange={handleSkillChange}
                id="skill-autocomplete"
                options={skillsData}
                sx={{ width: "60%", mx: "auto" }}
                renderInput={(params) => <TextField {...params} label="Select Skill Name" />}
              />
              {skill === null ? null : (
                <Box sx={{ mt: 2, pt: 2 }}>
                  {skillEmployeeResponseData.length === 0 ? (
                    <>
                      <Box
                        sx={{
                          mt: 12,
                          left: "50%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <NothingToDisplayComponent />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          width: "98%",
                          display: "flex",
                          mx: 2,
                          mt: 2,
                        }}
                      >
                        <Box sx={{ width: "20%" }}></Box>
                        <Box sx={{ width: "70%", display: "flex" }}>
                          <Box
                            sx={{
                              width: "90%",
                              mr: 2.7,
                              ml: 1,
                              display: "flex",
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            {ratingLabel.map((data, index) => {
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "20%",
                                  }}
                                >
                                  <Typography
                                    key={index}
                                    sx={{ mr: 0.3 }}
                                    fontSize={13}
                                    fontWeight={"bold"}
                                  >
                                    {data.label}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                          <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography fontSize={13} fontWeight={"bold"}>Average score</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography fontSize={13} fontWeight={"bold"}>No. of questions</Typography>
                      </Box>
                      </Box>
                      {skillEmployeeResponseData[0].employees.map((emp, index) => {
                          const totalQuestions = (emp.questions as any).length;
                          const totalPossible = totalQuestions * 5;
                        const managerStars = (emp.questions as any).reduce((sum: number, q: any) => {
                              const v = parseInt(q?.manager_skill_response || "0", 10);
                              return sum + (isNaN(v) ? 0 : v);
                        }, 0);
                        const managerPercentage = totalPossible > 0 ? (managerStars / totalPossible) * 100 : 0;
                        const managerRatedCount = (emp.questions as any).filter((q: any) => q?.manager_skill_response != null && q?.manager_skill_response !== "0").length;
                          const isManagerComplete = managerRatedCount === totalQuestions;
                        const empAnswered = (emp.questions as any).filter((q: any) => q.employee_skill_response !== null).length;
                        const empIdForExpand: any = (emp as any).user_id ?? index;
                        const isExpanded = expandedSkillEmpId === empIdForExpand;
                        const variationValues = (emp.questions as any[])
                          .map((q: any) =>
                            getRatingVariation(q.employee_skill_response, q.manager_skill_response)
                          )
                          .filter((v): v is number => v !== null);
                        const avgVariation =
                          variationValues.length > 0
                            ? variationValues.reduce((sum, v) => sum + v, 0) / variationValues.length
                            : null;
                        const totalDeviationStars = variationValues.reduce(
                          (sum, v) => sum + (v * 4) / 100,
                          0
                        );
                          return (
                          <Card key={empIdForExpand} sx={{ m: 2, p: 2 }} elevation={4}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                    <Box sx={{ width: "20%" }}>
                                <Typography variant="h6" color="primary" fontWeight={"bold"}>
                                        {emp.employee_name}
                                      </Typography>
                                      {avgVariation !== null && (
                                        <Tooltip title={`Overall difference: ${totalDeviationStars.toFixed(2)}`}>
                                          <Typography
                                            variant="caption"
                                            sx={{ color: getVariationColor(avgVariation), fontWeight: "bold" }}
                                          >
                                            Avg {getVariationText(parseFloat(avgVariation.toFixed(2)))}
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Box>
                              <Box sx={{ width: "70%", mx: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                      <Box sx={{ display: "grid", gridTemplateColumns: "80px 1fr 60px", alignItems: "center", columnGap: 1 }}>
                                        <Typography variant="caption" fontWeight={"bold"} sx={{ textAlign: "right" }}>Employee</Typography>
                                        <Box sx={{ width: "100%", position: "relative" }}>
                                    <LinearProgress variant="determinate" value={parseFloat(emp.percentage)} sx={{ flexGrow: 1, mr: 2 }} />
                                    <div className="skill-info-color" style={{ position: "absolute", top: "50%", transform: `translate(-50%, -50%)`, left: `${parseFloat(emp.percentage) > 50 ? parseFloat(emp.percentage) - 1.5 : parseFloat(emp.percentage)}%`, width: "12px", height: "12px", borderRadius: "50%" }} />
                                        </Box>
                                  <Typography variant="body2" fontWeight={"bold"}>{((parseFloat(emp.percentage as any) / 100) * 5).toFixed(1)}/5</Typography>
                                      </Box>
                                      <Box sx={{ display: "grid", gridTemplateColumns: "80px 1fr 60px", alignItems: "center", columnGap: 1 }}>
                                        <Typography variant="caption" fontWeight={"bold"} sx={{ textAlign: "right" }}>Manager</Typography>
                                        <Box sx={{ width: "100%", position: "relative" }}>
                                    <LinearProgress variant="determinate" value={managerPercentage} sx={{ flexGrow: 1, mr: 2, ...(isManagerComplete ? {} : { backgroundColor: "#ffebee", "& .MuiLinearProgress-bar": { backgroundColor: "#d32f2f" } }) }} />
                                    <div className="skill-info-color" style={{ position: "absolute", top: "50%", transform: `translate(-50%, -50%)`, left: `${managerPercentage > 50 ? managerPercentage - 1.5 : managerPercentage}%`, width: "12px", height: "12px", borderRadius: "50%", ...(isManagerComplete ? {} : { backgroundColor: "#d32f2f" }) }} />
                                        </Box>
                                  <Typography variant="body2" fontWeight={"bold"}>{((managerPercentage / 100) * 5).toFixed(1)}/5</Typography>
                                      </Box>
                                    </Box>
                              <Box sx={{ width: "10%", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                                <Typography variant="h6" color={empAnswered === totalQuestions ? "success" : "error"}>
                                  {empAnswered}/{totalQuestions}
                                </Typography>
                                <ChevronRightIcon fontSize="medium" sx={{ cursor: "pointer", ml: 1, transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }} onClick={() => toggleSkillEmpExpand(empIdForExpand)} />
                                  </Box>
                            </Box>
                            {isExpanded && (
                              <Box sx={{ mt: 2, p: 1 }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", alignItems: "center", columnGap: 2, px: 1, py: 0.5 }}>
                                    <Box />
                                  <Typography align="center" variant="caption" fontWeight="bold">Employee</Typography>
                                  <Typography align="center" variant="caption" fontWeight="bold">Manager</Typography>
                                  </Box>
                                {(emp.questions as any).map((question: any, questionIndex: number) => (
                                  <Card key={question.skill_question_id} sx={{ mb: 2, p: 2, boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)", border: "1px solid #ccc" }} elevation={5}>
                                    {(() => {
                                      const variation = getRatingVariation(
                                        question.employee_skill_response,
                                        (question as any).manager_skill_response
                                      );
                                      return variation !== null ? (
                                        <Tooltip title={getVariationNumberText(variation)}>
                                          <Typography
                                            variant="caption"
                                            sx={{ color: getVariationColor(variation), fontWeight: "bold", ml: 1 }}
                                          >
                                            {getVariationText(variation)}
                                          </Typography>
                                        </Tooltip>
                                      ) : null;
                                    })()}
                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", alignItems: "center", columnGap: 2, m: 0.5, p: 0.5 }}>
                                      <Box>
                                        <Typography fontWeight={"bold"} variant="h6" sx={{ textAlign: "justify", wordBreak: "break-word" }}>
                                              {questionIndex + 1 + ") "}
                                              {question.question}
                                            </Typography>
                                          </Box>
                                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <Typography align="center" variant="caption" fontWeight="bold">Employee</Typography>
                                        <Rating name={`skillwise-emp-rating-${index}-${questionIndex}`} value={parseInt(question.employee_skill_response || "0")} readOnly size="large" className="rating-color" />
                                      </Box>
                                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <Typography align="center" variant="caption" fontWeight="bold">Manager</Typography>
                                        <Rating name={`skillwise-mgr-rating-${index}-${questionIndex}`} value={parseInt(((question as any).manager_skill_response) || "0")} readOnly size="large" className="rating-color" />
                                      </Box>
                                        </Box>
                                  </Card>
                                ))}
                                      </Box>
                            )}
                          </Card>
                                    );
                                  })}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
      </Box>
    </>
  );
};

export default ReviewEmployeesSkills;